import { Component, ElementRef, ViewChild, computed, effect, inject, signal, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { SolIcon } from '@sol/icons';
import { SolNavbarItem } from '@sol/components';
import { LabSearchFieldComponent } from '../../components/lab-search-field/lab-search-field.component';
import { LabIconComponent } from '../../components/lab-icon/lab-icon.component';
import { CommandPaletteService } from '../../services/command-palette.service';
import { apps } from '../../apps';

interface PaletteItem {
  readonly id: string;
  readonly label: string;
  readonly groupLabel: string;
  readonly icon?: SolIcon;
}

function flattenNavbar(items: SolNavbarItem[], groupLabel = ''): PaletteItem[] {
  return items.flatMap(item => {
    if (item.children.length > 0) {
      return flattenNavbar(item.children, item.displayName ?? groupLabel);
    }
    if (!item.displayName || !item.id) return [];
    return [{ id: item.id, label: item.displayName, groupLabel, icon: item.icon }];
  });
}

const navbar = apps[0].navbar;
const ALL_ITEMS: PaletteItem[] = navbar
  ? [...flattenNavbar(navbar.menu), ...flattenNavbar(navbar.system), ...flattenNavbar(navbar.account)]
  : [];

@Component({
  selector: 'lab-command-palette',
  standalone: true,
  imports: [LabSearchFieldComponent, LabIconComponent],
  templateUrl: './lab-command-palette.component.html',
  styleUrl: './lab-command-palette.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class LabCommandPaletteComponent {
  private readonly paletteService = inject(CommandPaletteService);
  private readonly router = inject(Router);

  protected readonly open = this.paletteService.open;
  protected readonly query = this.paletteService.query;
  protected readonly activeIndex = signal(0);

  protected readonly filteredItems = computed(() => {
    const q = this.query().trim().toLowerCase();
    if (!q) return ALL_ITEMS;
    return ALL_ITEMS.filter(item => item.label.toLowerCase().includes(q) || item.id.toLowerCase().includes(q));
  });

  @ViewChild(LabSearchFieldComponent) private searchField?: LabSearchFieldComponent;
  @ViewChild('surface') private surface?: ElementRef<HTMLDivElement>;

  constructor() {
    effect(() => {
      if (this.open()) {
        this.activeIndex.set(0);
        queueMicrotask(() => this.searchField?.focus());
      }
    });
  }

  protected onQueryChange(value: string): void {
    if (value === this.query()) return;
    this.paletteService.setQuery(value);
    this.activeIndex.set(0);
  }

  protected onKeydown(event: KeyboardEvent): void {
    const items = this.filteredItems();
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activeIndex.set((this.activeIndex() + 1) % Math.max(items.length, 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeIndex.set((this.activeIndex() - 1 + items.length) % Math.max(items.length, 1));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const item = items[this.activeIndex()];
      if (item) this.navigate(item.id);
    }
  }

  protected navigate(id: string): void {
    this.close();
    void this.router.navigate([`/${id}`]);
  }

  protected close(): void {
    this.paletteService.closePalette();
  }

  protected setActive(index: number): void {
    this.activeIndex.set(index);
  }
}
